import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { useI18n } from '../../i18n';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import './LoginPage.css';

function validatePassword(password: string, confirmPassword: string, t: (text: string) => string): string | null {
  if (password.length < 8) return t('Password must be at least 8 characters');
  if (!/[A-Z]/.test(password)) return t('Password must contain at least one uppercase letter');
  if (!/[a-z]/.test(password)) return t('Password must contain at least one lowercase letter');
  if (!/[0-9]/.test(password)) return t('Password must contain at least one number');
  if (password !== confirmPassword) return t('Passwords do not match');
  return null;
}

function getRecoveryLinkError(): string {
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const searchParams = new URLSearchParams(window.location.search);
  return (
    hashParams.get('error_description') ||
    hashParams.get('error') ||
    searchParams.get('error_description') ||
    searchParams.get('error') ||
    ''
  );
}

export default function ResetPasswordPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const recoveryLinkError = useMemo(() => getRecoveryLinkError(), []);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(recoveryLinkError);
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const passwordError = validatePassword(password, confirmPassword, t);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      await authService.updatePassword(password);
      setPassword('');
      setConfirmPassword('');
      setSuccess(t('Your password has been updated. You can continue to the portal.'));
    } catch (err) {
      setError((err as Error).message || t('Failed to update password'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-layout">
      <div className="auth-layout__lang">
        <LanguageSwitcher />
      </div>
      <form onSubmit={handleSubmit} className="auth-card">
        <div className="auth-card__logo">OdinsEye</div>
        <h1 className="auth-card__title">{t('Reset password')}</h1>
        <p className="auth-card__subtitle">
          {t('Choose a new password for your operations workspace.')}
        </p>

        {error && <div className="msg--error">{error}</div>}
        {success && <div className="msg--success">{success}</div>}

        <div className="login__form-group">
          <label className="form__label" htmlFor="new-password">{t('New password')}</label>
          <input
            id="new-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="form__input"
            placeholder={t('Enter your new password')}
            autoComplete="new-password"
          />
        </div>

        <div className="login__form-group">
          <label className="form__label" htmlFor="confirm-password">{t('Confirm Password')}</label>
          <input
            id="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="form__input"
            placeholder={t('Confirm your new password')}
            autoComplete="new-password"
          />
        </div>

        <button
          type="submit"
          disabled={isLoading || Boolean(success)}
          className="btn btn--primary btn--full-width login__submit"
        >
          {isLoading ? t('Updating password...') : t('Update password')}
        </button>

        <button
          type="button"
          className="login__link-button login__link-button--center"
          onClick={() => navigate(success ? '/dashboard' : '/sign-in')}
        >
          {success ? t('Continue to portal') : t('Back to sign in')}
        </button>
      </form>
    </div>
  );
}
